import React from "react";
import { Link } from "react-router-dom";
import { Tag, Gift, ShieldCheck, CreditCard } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { FAQ } from "@/components/FAQ";
import { TeacherRates } from "@/components/TeacherRates";
import { PlayAlongBanner } from "@/components/PlayAlongPromo";
import { PianoVideos } from "@/components/PianoVideos";
import { CourseCheckout } from "@/components/CourseCheckout";
import { OneToOneCheckout } from "@/components/OneToOneCheckout";
import { PopularLine } from "@/components/PopularBadge";

const OFFERS = [
  { icon: Gift, t: "Refer a friend after booking", d: "Booked a course? Refer someone — when they sign up they save £50 and you get a free 1:1 with Jason" },
  { icon: Tag, t: "2-course bundle £850", d: "Any two courses — even across instruments" },
  { icon: ShieldCheck, t: "Teacher licences £400", d: "Per class, email-verified" },
];

const OneToOne = () => (
  <div id="one-to-one" className="mt-14 overflow-hidden rounded-3xl border border-border bg-card" data-testid="pricing-one-to-one">
    <div className="relative h-56 sm:h-72">
      <img src="/media/jt-piano-lessons.jpg" alt="Jason Thompson playing piano" className="h-full w-full object-cover object-center" data-testid="one-to-one-image" />
      <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent" />
    </div>
    <div className="flex flex-wrap items-start justify-between gap-6 p-8 pt-2">
      <div className="max-w-xl">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Private tuition</p>
        <h2 className="mt-2 font-serif text-3xl">1:1 sessions with Jason</h2>
        <p className="mt-3 text-muted-foreground">
          One-to-one lessons tailored to you — <span className="font-semibold text-foreground">£70 per hour</span>. Book as many hours as you like, then we'll arrange a time to suit you.
        </p>
      </div>
      <OneToOneCheckout />
    </div>
    <div className="border-t border-border px-8 pb-8"><PianoVideos limit={3} compact /></div>
  </div>
);

export default function Pricing() {
  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 sm:px-8 py-10 lg:py-14">
        <div className="relative mb-10 overflow-hidden rounded-3xl border border-border">
          <img src="/media/jt-playing-7.webp" alt="Jason Thompson playing two keyboards" className="h-64 w-full object-cover object-[center_22%] sm:h-[28rem]" data-testid="pricing-hero-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
        </div>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Enrol & unlock</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">Course pricing</h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">
          Each course is <span className="font-bold text-foreground">£500</span> (£50 per lesson) — a full 10-lesson path.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <p className="rounded-full bg-primary/15 px-4 py-2 text-sm font-semibold text-primary" data-testid="pricing-strapline">Sign up to 2 courses and save £150!</p>
          <p className="text-sm text-muted-foreground"><span className="font-bold text-foreground">Teacher discount available.</span></p>
          <Link to="/payment" data-testid="pricing-go-payment" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90">
            <CreditCard size={15} /> Go to Payment
          </Link>
        </div>
        <PopularLine />
        <div className="mt-6 flex flex-wrap gap-3">
          {OFFERS.map((b) => (
            <div key={b.t} className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2">
              <b.icon size={16} className="text-primary" />
              <span className="text-sm font-medium">{b.t}</span>
              <span className="hidden text-xs text-muted-foreground sm:inline">· {b.d}</span>
            </div>
          ))}
        </div>
        <CourseCheckout />
        <PlayAlongBanner testId="pricing-playalong-banner" />
        <TeacherRates />
        <OneToOne />
        <div className="mt-16"><FAQ /></div>
      </div>
    </div>
  );
}
